import { apiClient } from './client';

/**
 * 직책(role) 마스터 API (인사, migration 144 · 2026-08-29).
 *
 * 서버: `apps/chat-server/src/modules/hr/job-roles.routes.ts`.
 *
 * ★★ **직위(rank)와 다른 축이다.** 직위는 서열(승진·급여), 직책은 조직에 대한 보임
 *    (발령·결재권). 영문 이름을 둘 다 `position` 으로 옮기면 섞인다 — 여기서는 role 이다.
 * ★ **읽기는 전 직원**(호칭·서열을 그리는 데 쓰인다), 쓰기는 담당자 — 403.
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼킨다.
 */

/** 직책이 붙는 조직 단위. */
export type RoleScope = 'TEAM' | 'DIVISION' | 'COMPANY';

export const SCOPE_LABEL: Record<RoleScope, string> = {
  TEAM: '팀', DIVISION: '부서', COMPANY: '전사',
};

export interface JobRoleDto {
  id: string;
  name: string;
  /** 부르는 이름. 비어 있으면 `name` 이 곧 호칭이다. */
  callName: string | null;
  /** 클수록 높다 — **직위 마스터의 level 과 같은 뜻**이라야 두 축을 견줄 수 있다. */
  level: number;
  scopeKind: RoleScope;
  /**
   * ★ 그 조직의 «장(長)» 인가 — **참일 때만** 보임이 결재 사다리(팀장·부서장)를 세운다.
   *   파트장·TF장은 거짓이다(그 자리에 결재권이 딸리지 않는다).
   */
  isHead: boolean;
  active: boolean;
  note: string | null;
}

export interface RoleTemplateItem {
  name: string;
  level: number;
  scopeKind: RoleScope;
  isHead: boolean;
}

export async function fetchJobRoles(all = false): Promise<JobRoleDto[]> {
  const { data } = await apiClient.get<{ roles: JobRoleDto[] }>('/hr/job-roles', {
    params: all ? { all: '1' } : undefined,
  });
  return data.roles;
}

export async function fetchRoleTemplate(): Promise<RoleTemplateItem[]> {
  const { data } = await apiClient.get<{ items: RoleTemplateItem[] }>('/hr/job-roles/templates');
  return data.items;
}

export async function createJobRole(body: {
  name: string; callName?: string | null; level?: number;
  scopeKind?: RoleScope; isHead?: boolean; note?: string | null;
}): Promise<JobRoleDto> {
  const { data } = await apiClient.post<JobRoleDto>('/hr/job-roles', body);
  return data;
}

export async function updateJobRole(id: string, body: {
  name?: string; callName?: string | null; level?: number;
  scopeKind?: RoleScope; isHead?: boolean; active?: boolean; note?: string | null;
}): Promise<JobRoleDto> {
  const { data } = await apiClient.patch<JobRoleDto>(`/hr/job-roles/${id}`, body);
  return data;
}

/**
 * 기본 묶음 담기 — **없는 것만 보탠다.**
 * ★ 무엇을 담고 무엇을 건너뛰었는지 돌려준다(눌렀는데 아무 말이 없으면 됐는지 모른다).
 */
export async function adoptRoleTemplate(names?: string[]): Promise<{ adopted: string[]; skipped: string[] }> {
  const { data } = await apiClient.post<{ adopted: string[]; skipped: string[] }>(
    '/hr/job-roles/adopt', names ? { names } : {},
  );
  return data;
}
